import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  title?: string;
  duration?: number;
}

export interface ToastOptions {
  type: Toast["type"];
  title?: string;
  message: string;
  duration?: number;
}

export interface ShowToast {
  (options: ToastOptions): string;
  (message: string, type?: Toast["type"], duration?: number): string;
}

interface ToastState {
  toasts: Toast[];
  showToast: ShowToast;
  dismissToast: (id: string) => void;
  clearAll: () => void;
}

const DEFAULT_DURATION = 3000;

export const useToastStore = create<ToastState>()(
  devtools(
    (set, get) => {
      const showToast = ((
        messageOrOptions: string | ToastOptions,
        type: Toast["type"] = "info",
        duration: number = DEFAULT_DURATION,
      ): string => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const toast: Toast =
          typeof messageOrOptions === "string"
            ? { id, message: messageOrOptions, type, duration }
            : {
                id,
                message: messageOrOptions.message,
                type: messageOrOptions.type,
                title: messageOrOptions.title,
                duration: messageOrOptions.duration ?? DEFAULT_DURATION,
              };

        set((state) => ({ toasts: [...state.toasts, toast] }));

        if (toast.duration && toast.duration > 0) {
          setTimeout(() => get().dismissToast(id), toast.duration);
        }

        return id;
      }) as ShowToast;

      return {
        toasts: [],
        showToast,
        dismissToast: (id) =>
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          })),
        clearAll: () => set({ toasts: [] }),
      };
    },
    { name: "ToastStore" },
  ),
);
